/**
 * Funnel-app override for the `user_sync` renderer.
 *
 * Routes to the correct sync implementation based on the application's
 * `authProvider` config:
 *   - `"kinde"` (default): Kinde-based user check + create via product API
 *   - `"clerk"`: Calls funnel-api `/api/clerk/signup`, stores sign-in token
 *
 * The Kinde path consolidates the full user-sync lifecycle:
 *   1. If `user_exists` is already `true` in answers, skip.
 *   2. Check if the authenticated user already exists in the product backend.
 *   3. If new and a display name is available, create the user.
 *   4. Write `user_exists` into answers for downstream branching rules.
 *
 * Step 2 runs on every entry, including re-entries where a previous attempt
 * recorded `user_exists: false`: that attempt's create may have committed
 * upstream and still returned an error, so retrying without re-checking can
 * duplicate the account (NX-12245).
 *
 * On any API error the step writes `skipped_error` and advances so the
 * funnel is never blocked.
 */
import { userSyncSchema, type UserSyncData } from "@nextday/survey-kit";
import type { RendererProps, StepRenderer } from "@nextday/survey-kit";
import { useCallback, useEffect, useRef, type ReactElement } from "react";

import { resolveDisplayName } from "@src/auth/ndapiSync";
import { usePostAuthUserSync, USER_SYNC_STATUS } from "@src/auth/usePostAuthUserSync";
import { useApplicationContext } from "@src/funnel/useApplicationContext";
import { ClerkUserSyncComponent } from "@src/funnel/clerkUserSync";

export { resolveDisplayName };

const KindeUserSyncComponent = ({
  data,
  answers,
  session,
  onAdvance,
  onAnswer,
}: RendererProps<UserSyncData>): ReactElement => {
  const { checkUser, createUserAccount } = usePostAuthUserSync();
  const triggeredRef = useRef(false);

  const userAlreadyExists = answers.user_exists === true;

  const displayName = resolveDisplayName(answers, data.nameAnswerKey);

  const doCreate = useCallback(() => {
    void createUserAccount({
      applicationId: session.application_id,
      anonymousId: session.anonymous_id,
      displayName: displayName || "User",
    }).then((createStatus) => {
      onAnswer("user_exists", { value: createStatus !== USER_SYNC_STATUS.ERROR });
      onAnswer(data.answerKey, {
        value: createStatus === USER_SYNC_STATUS.ERROR ? "skipped_error" : createStatus,
      });
      onAdvance();
    });
  }, [
    createUserAccount,
    session.application_id,
    session.anonymous_id,
    displayName,
    onAnswer,
    data.answerKey,
    onAdvance,
  ]);

  const attemptSync = useCallback(() => {
    if (userAlreadyExists) {
      onAnswer(data.answerKey, { value: "skipped" });
      onAdvance();
      return;
    }

    void checkUser({
      applicationId: session.application_id,
      anonymousId: session.anonymous_id,
    }).then((checkStatus) => {
      if (checkStatus === USER_SYNC_STATUS.EXISTS) {
        onAnswer("user_exists", { value: true });
        onAnswer(data.answerKey, { value: "exists" });
        onAdvance();
        return;
      }

      if (checkStatus === USER_SYNC_STATUS.ERROR) {
        onAnswer("user_exists", { value: false });
        onAnswer(data.answerKey, { value: "skipped_error" });
        onAdvance();
        return;
      }

      if (!displayName) {
        onAnswer("user_exists", { value: false });
        onAnswer(data.answerKey, { value: "needs_name" });
        onAdvance();
        return;
      }

      doCreate();
    });
  }, [
    userAlreadyExists,
    checkUser,
    doCreate,
    session.application_id,
    session.anonymous_id,
    displayName,
    onAnswer,
    data.answerKey,
    onAdvance,
  ]);

  useEffect(() => {
    if (triggeredRef.current) return;
    triggeredRef.current = true;
    attemptSync();
  }, [attemptSync]);

  return (
    <div className="flex flex-col items-center gap-3 p-6" role="status" aria-label="Syncing">
      <div className="size-8 animate-spin rounded-full border-2 border-white/20 border-t-white" />
      <p className="text-sm opacity-80">{data.message ?? "Setting up your profile\u2026"}</p>
    </div>
  );
};

const UserSyncRouterComponent = (props: RendererProps<UserSyncData>): ReactElement => {
  const ctx = useApplicationContext();
  const isClerk =
    ctx.status === "ready" &&
    ctx.application.authProvider === "clerk" &&
    !!ctx.application.clerkPublishableKey;

  if (isClerk) {
    return <ClerkUserSyncComponent {...props} />;
  }

  return <KindeUserSyncComponent {...props} />;
};

export const userSyncOverride: StepRenderer<UserSyncData> = {
  type: "user_sync",
  dataSchema: userSyncSchema,
  label: "User sync (API wrapper)",
  Component: UserSyncRouterComponent,
};

export default userSyncOverride;
